// src/components/HUD/HUDAnalyticsDashboard.js
// UPDATED: 2026-08-09 — removed all isAdmin guards. Every feature is fully
// functional for all authenticated users (admin and hud_analyst alike).
// All endpoints now use /hud/* which accepts both adminToken and hudToken.
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import HUDSourceDataPanel from './HUDSourceDataPanel';
import HUDScriptCard from './HUDScriptCard';
import HUDScriptManager from './HUDScriptManager';
const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';
const PDF_TYPES = [
  { key: 'opinions_sefa',     label: 'Opinion & SEFA',    priority: 1 },
  { key: 'findings',          label: 'Findings',          priority: 2 },
  { key: 'mda',               label: 'MD&A',              priority: 3 },
  { key: 'corrective_action', label: 'Corrective Action', priority: 4 },
  { key: 'financials',        label: 'Financials',        priority: 5 },
  { key: 'footnotes',         label: 'Footnotes',         priority: 6 },
  { key: 'pension',           label: 'Pension',           priority: 7 },
];
const OPINION_COLORS = {
  unqualified: { bg: '#EAF3DE', text: '#27500A', label: 'Unqualified ✓' },
  qualified:   { bg: '#FAEEDA', text: '#633806', label: 'Qualified ⚠' },
  adverse:     { bg: '#FCEBEB', text: '#A32D2D', label: 'Adverse ✗' },
  disclaimer:  { bg: '#FCEBEB', text: '#A32D2D', label: 'Disclaimer ✗' },
};
const STATUS_STYLES = {
  'Approved': { bg: '#EAF3DE', text: '#27500A' },
  'Review':   { bg: '#FAEEDA', text: '#633806' },
};
export default function HUDAnalyticsDashboard({ currentUser, onLogout }) {
  const [phaList,           setPhaList]           = useState([]);
  const [phaListLoading,    setPhaListLoading]    = useState(true);
  const [selectedPha,       setSelectedPha]       = useState(null);
  const [uploads,           setUploads]           = useState({});
  const [analyses,          setAnalyses]          = useState({});
  const [loading,           setLoading]           = useState({});
  const [uploading,         setUploading]         = useState({});
  const [socketStatus,      setSocketStatus]      = useState('disconnected');
  const [events,            setEvents]            = useState([]);
  const [registeredScripts, setRegisteredScripts] = useState([]);
  const socketRef = useRef(null);

  const fetchPhaList = async () => {
    try {
      const res = await axios.get(`${API_URL}/hud/submissions`, { withCredentials: true });
      const list = (res.data.submissions || []).map(s => ({
        code: s.phaCode, name: s.phaCode,
        sttsName: s.sttsName || null, rtTypeFye: s.rtTypeFye || null,
        combinedStatus: s.combinedStatus || 'pending',
      })).sort((a, b) => a.code.localeCompare(b.code));
      setPhaList(list);
      if (list.length > 0) setSelectedPha(prev => prev || list[0]);
    } catch (err) {
      console.error('[HUD] Failed to fetch PHA list:', err.message);
    } finally {
      setPhaListLoading(false);
    }
  };

  const fetchRegisteredScripts = async () => {
    try {
      const res = await axios.get(`${API_URL}/hud/list-scripts`, { withCredentials: true });
      setRegisteredScripts(res.data.scripts || []);
    } catch (err) {
      console.error('[HUD] Failed to fetch script list:', err.message);
    }
  };

  useEffect(() => { fetchPhaList(); fetchRegisteredScripts(); }, []);

  useEffect(() => {
    const socket = io(window.location.origin, { withCredentials: true, transports: ['websocket', 'polling'] });
    socketRef.current = socket;
    socket.on('connect',    () => setSocketStatus('connected'));
    socket.on('disconnect', () => setSocketStatus('disconnected'));
    socket.on('hud_pdf_uploaded', (data) => {
      setEvents(prev => [{ ...data, event: 'PDF Uploaded' }, ...prev.slice(0, 9)]);
      setUploads(prev => ({ ...prev, [data.phaCode]: { ...(prev[data.phaCode] || {}), [data.pdfType]: data.fileName } }));
    });
    socket.on('hud_pdf_analysis_complete', (data) => {
      setEvents(prev => [{ ...data, event: 'Analysis Complete' }, ...prev.slice(0, 9)]);
      setAnalyses(prev => ({ ...prev, [data.phaCode]: data.analysis }));
      setLoading(prev => ({ ...prev, [data.phaCode]: false }));
    });
    socket.on('hud_pdf_analysis_error', (data) => {
      setEvents(prev => [{ ...data, event: 'Analysis Error' }, ...prev.slice(0, 9)]);
      setLoading(prev => ({ ...prev, [data.phaCode]: false }));
    });
    socket.on('hud_source_data_uploaded', (data) => {
      setEvents(prev => [{ ...data, event: 'Source Data Uploaded', phaCode: `${data.phaCount} PHAs` }, ...prev.slice(0, 9)]);
      fetchPhaList();
    });
    return () => socket.disconnect();
  }, []);

  const handleUpload = async (phaCode, pdfType, file) => {
    const key = `${phaCode}_${pdfType}`;
    setUploading(prev => ({ ...prev, [key]: true }));
    try {
      const formData = new FormData();
      formData.append('pdf', file);
      await axios.post(
        `${API_URL}/hud/upload-pdf/${phaCode}/${pdfType}`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setUploads(prev => ({ ...prev, [phaCode]: { ...(prev[phaCode] || {}), [pdfType]: file.name } }));
    } catch (err) {
      alert(`Upload failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setUploading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleAnalyze = async (phaCode) => {
    setLoading(prev => ({ ...prev, [phaCode]: true }));
    try {
      const res = await axios.post(`${API_URL}/hud/analyze-pdf`, { phaCode }, { withCredentials: true });
      if (res.data.success) setAnalyses(prev => ({ ...prev, [phaCode]: res.data.analysis }));
    } catch (err) {
      alert(`Analysis failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(prev => ({ ...prev, [phaCode]: false }));
    }
  };

  const currentPha   = selectedPha || {};
  const phaUploads   = uploads[currentPha.code] || {};
  const phaAnalysis  = analyses[currentPha.code];
  const phaAnalyzing = loading[currentPha.code];
  const uploadCount  = Object.keys(phaUploads).length;
  const opinionStyle = phaAnalysis ? (OPINION_COLORS[phaAnalysis.auditor_opinion] || OPINION_COLORS.qualified) : null;

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', maxWidth: 1200, margin: '0 auto', padding: 24, color: '#2C2C2A' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>HUD Analytics Dashboard</h1>
          <p style={{ margin: '4px 0 0', color: '#5F5E5A', fontSize: 13 }}>
            REAC PHA Submission Review — Layer 1: ACL Analytics &amp; Layer 2: Audit PDF Analysis
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 20,
            background: socketStatus === 'connected' ? '#E1F5EE' : '#FCEBEB',
            fontSize: 12, fontWeight: 500,
            color: socketStatus === 'connected' ? '#085041' : '#A32D2D',
          }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'currentColor', display: 'inline-block' }} />
            {socketStatus === 'connected' ? 'Live' : 'Disconnected'}
          </div>
          {currentUser && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#2C2C2A' }}>{currentUser.name || currentUser.email}</div>
              <div style={{ fontSize: 10, color: '#888780' }}>{currentUser.organization || 'HUD Analyst'}</div>
            </div>
          )}
          {onLogout && (
            <button
              onClick={onLogout}
              style={{ padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600, background: '#fff', color: '#5F5E5A', border: '1px solid #D3D1C7', cursor: 'pointer' }}
            >
              Sign out
            </button>
          )}
        </div>
      </div>

      {/* Source Data Upload */}
      <HUDSourceDataPanel onUploadComplete={() => {}} />

      {/* ACL Script Manager */}
      <HUDScriptManager onScriptsChanged={(scripts) => setRegisteredScripts(scripts)} />

      {/* Layer 1 ACL Script Cards */}
      <div style={{ background: '#fff', border: '1px solid #D3D1C7', borderRadius: '10px', padding: '16px 20px', marginBottom: '20px' }}>
        <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: '700', color: '#1a1a18' }}>Layer 1 — ACL Analytics</p>
        <p style={{ margin: '0 0 14px', fontSize: '11px', color: '#888780' }}>
          Run ACL scripts against the FDS dataset on the Azure Gov VM ({registeredScripts.length} scripts available)
        </p>
        {registeredScripts.length === 0 ? (
          <p style={{ fontSize: '12px', color: '#888780' }}>Loading scripts...</p>
        ) : (
          registeredScripts.map(s => <HUDScriptCard key={s.scriptName} script={s.scriptName} />)
        )}
      </div>

      {/* PHA Sidebar + Main Panel */}
      {phaListLoading ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: '#888780', fontSize: 13 }}>Loading PHAs...</div>
      ) : phaList.length === 0 ? (
        <div style={{ padding: '32px 24px', background: '#F9F8F6', border: '1px solid #D3D1C7', borderRadius: 10, textAlign: 'center', color: '#888780', fontSize: 13 }}>
          No PHAs loaded yet. Upload a Source_Data.xlsx file above to populate the PHA list.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 20 }}>
          {/* Sidebar */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#5F5E5A', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>
              PHAs in Review ({phaList.length})
            </div>
            {phaList.map(pha => {
              const a = analyses[pha.code];
              const u = uploads[pha.code];
              const uploadCnt = u ? Object.keys(u).length : 0;
              const isSelected = selectedPha && pha.code === selectedPha.code;
              const oStyle = a ? (OPINION_COLORS[a.auditor_opinion] || OPINION_COLORS.qualified) : null;
              const sStyle = pha.sttsName ? (STATUS_STYLES[pha.sttsName] || null) : null;
              return (
                <div key={pha.code} onClick={() => setSelectedPha(pha)} style={{
                  padding: '10px 14px', borderRadius: 8, marginBottom: 6, cursor: 'pointer',
                  border: `1.5px solid ${isSelected ? '#1D9E75' : '#D3D1C7'}`,
                  background: isSelected ? '#E1F5EE' : '#fff', transition: 'all .15s',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, fontSize: 13, color: isSelected ? '#085041' : '#2C2C2A' }}>{pha.code}</span>
                    {a && oStyle ? (
                      <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, fontWeight: 600, background: oStyle.bg, color: oStyle.text }}>{a.auditor_opinion}</span>
                    ) : sStyle ? (
                      <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, fontWeight: 600, background: sStyle.bg, color: sStyle.text }}>{pha.sttsName}</span>
                    ) : null}
                  </div>
                  <div style={{ fontSize: 10, color: '#888885', marginTop: 3 }}>
                    {pha.rtTypeFye && <span style={{ marginRight: 8, color: '#5F5E5A' }}>{pha.rtTypeFye}</span>}
                    {uploadCnt > 0 && <span>{uploadCnt} PDF{uploadCnt !== 1 ? 's' : ''} uploaded</span>}
                    {a?.is_mtw && <span style={{ marginLeft: 6, color: '#BA7517', fontWeight: 600 }}>MTW</span>}
                    {a?.escalate_to_analyst && <span style={{ marginLeft: 6, color: '#A32D2D', fontWeight: 600 }}>⚠ Escalate</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Main Panel */}
          {selectedPha && (
            <div>
              {/* PHA Header */}
              <div style={{ background: '#fff', border: '1px solid #D3D1C7', borderRadius: 12, padding: 20, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h2 style={{ margin: 0, fontSize: 18 }}>
                      {currentPha.code}
                      {currentPha.sttsName && (
                        <span style={{ marginLeft: 12, fontSize: 12, padding: '3px 10px', borderRadius: 12, fontWeight: 600, verticalAlign: 'middle', background: STATUS_STYLES[currentPha.sttsName]?.bg || '#F1EFE8', color: STATUS_STYLES[currentPha.sttsName]?.text || '#3d3d3a' }}>
                          {currentPha.sttsName}
                        </span>
                      )}
                      {currentPha.rtTypeFye && (
                        <span style={{ marginLeft: 8, fontSize: 11, padding: '3px 10px', borderRadius: 12, fontWeight: 600, verticalAlign: 'middle', background: '#EEEDFE', color: '#3C3489' }}>
                          {currentPha.rtTypeFye}
                        </span>
                      )}
                    </h2>
                    <p style={{ margin: '4px 0 0', color: '#5F5E5A', fontSize: 13 }}>{uploadCount} of {PDF_TYPES.length} PDF sections uploaded</p>
                  </div>
                  <button
                    onClick={() => handleAnalyze(currentPha.code)}
                    disabled={uploadCount === 0 || phaAnalyzing}
                    style={{
                      padding: '10px 22px', borderRadius: 8, border: 'none',
                      cursor: uploadCount === 0 || phaAnalyzing ? 'not-allowed' : 'pointer',
                      background: uploadCount === 0 || phaAnalyzing ? '#D3D1C7' : '#1D9E75',
                      color: '#fff', fontWeight: 600, fontSize: 14,
                    }}
                  >
                    {phaAnalyzing ? 'Analyzing...' : '🔍 Analyze with AI'}
                  </button>
                </div>
              </div>

              {/* PDF Sections */}
              <div style={{ background: '#fff', border: '1px solid #D3D1C7', borderRadius: 12, padding: 20, marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 14 }}>PDF Sections</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {PDF_TYPES.map(pt => {
                    const uploaded = phaUploads[pt.key];
                    const key = `${currentPha.code}_${pt.key}`;
                    const isUploading = uploading[key];
                    return (
                      <div key={pt.key} style={{
                        padding: '10px 14px', borderRadius: 8,
                        border: `1px solid ${uploaded ? '#1D9E75' : '#D3D1C7'}`,
                        background: uploaded ? '#E1F5EE' : '#F9F8F6',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      }}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: uploaded ? '#085041' : '#2C2C2A' }}>
                            {pt.priority === 1 && <span style={{ color: '#A32D2D', marginRight: 4 }}>★</span>}
                            {pt.label}
                          </div>
                          <div style={{ fontSize: 11, color: '#888885', marginTop: 2 }}>{uploaded ? '✓ Uploaded' : 'Not uploaded'}</div>
                        </div>
                        <label style={{
                          padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                          background: uploaded ? '#1D9E75' : '#fff',
                          color: uploaded ? '#fff' : '#2C2C2A',
                          border: `1px solid ${uploaded ? '#1D9E75' : '#D3D1C7'}`,
                          cursor: isUploading ? 'not-allowed' : 'pointer',
                        }}>
                          {isUploading ? '...' : uploaded ? 'Replace' : 'Upload'}
                          <input
                            type="file" accept=".pdf" style={{ display: 'none' }}
                            disabled={isUploading}
                            onChange={(e) => { if (e.target.files[0]) handleUpload(currentPha.code, pt.key, e.target.files[0]); e.target.value = ''; }}
                          />
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Analysis Results */}
              {phaAnalysis && (
                <div style={{ background: '#fff', border: `1.5px solid ${phaAnalysis.escalate_to_analyst ? '#D85A30' : '#1D9E75'}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>AI Analysis Results — {currentPha.code}</div>
                    <span style={{ padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: phaAnalysis.escalate_to_analyst ? '#FCEBEB' : '#EAF3DE', color: phaAnalysis.escalate_to_analyst ? '#A32D2D' : '#27500A' }}>
                      {phaAnalysis.escalate_to_analyst ? '⚠ Analyst Queue' : '✓ Auto-Clear Eligible'}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
                    {[
                      { label: 'Auditor Opinion',   value: opinionStyle?.label, bg: opinionStyle?.bg, color: opinionStyle?.text },
                      { label: 'Accounting Basis',  value: phaAnalysis.accounting_basis?.replace('_', ' '), bg: '#EEEDFE', color: '#3C3489' },
                      { label: 'MTW Designation',   value: phaAnalysis.is_mtw ? 'Yes — MTW Authority' : 'No', bg: phaAnalysis.is_mtw ? '#FAEEDA' : '#F1EFE8', color: phaAnalysis.is_mtw ? '#633806' : '#3d3d3a' },
                      { label: 'Findings Present',  value: phaAnalysis.findings_present ? `Yes — ${phaAnalysis.finding_count} finding(s)` : 'None', bg: phaAnalysis.findings_present ? '#FCEBEB' : '#EAF3DE', color: phaAnalysis.findings_present ? '#A32D2D' : '#27500A' },
                      { label: 'New Long-Term Debt',value: phaAnalysis.new_long_term_debt ? `Yes — $${(phaAnalysis.new_long_term_debt_amount || 0).toLocaleString()}` : 'No', bg: phaAnalysis.new_long_term_debt ? '#FAEEDA' : '#F1EFE8', color: phaAnalysis.new_long_term_debt ? '#633806' : '#3d3d3a' },
                      { label: 'Corrective Action', value: phaAnalysis.corrective_action_plan_complete ? 'Complete' : 'Incomplete', bg: phaAnalysis.corrective_action_plan_complete ? '#EAF3DE' : '#FCEBEB', color: phaAnalysis.corrective_action_plan_complete ? '#27500A' : '#A32D2D' },
                    ].map((item, i) => (
                      <div key={i} style={{ padding: '10px 14px', borderRadius: 8, background: item.bg }}>
                        <div style={{ fontSize: 10, color: '#5F5E5A', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>{item.label}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: item.color }}>{item.value}</div>
                      </div>
                    ))}
                  </div>
                  {phaAnalysis.escalate_to_analyst && phaAnalysis.escalation_reason && (
                    <div style={{ padding: '10px 14px', borderRadius: 8, background: '#FAECE7', border: '1px solid #D85A30' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#712B13', marginBottom: 4 }}>Escalation Reason</div>
                      <div style={{ fontSize: 12, color: '#712B13' }}>{phaAnalysis.escalation_reason}</div>
                    </div>
                  )}
                  {phaAnalysis.findings && phaAnalysis.findings.length > 0 && (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Findings Detail</div>
                      {phaAnalysis.findings.map((f, i) => (
                        <div key={i} style={{ padding: '8px 12px', borderRadius: 6, background: '#FCEBEB', marginBottom: 6 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#A32D2D' }}>{f.type?.replace('_', ' ').toUpperCase()}</div>
                          <div style={{ fontSize: 12, marginTop: 2 }}>{f.description}</div>
                          {f.federal_program && <div style={{ fontSize: 11, color: '#5F5E5A', marginTop: 2 }}>Program: {f.federal_program}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Live Event Log */}
              {events.length > 0 && (
                <div style={{ background: '#F9F8F6', border: '1px solid #D3D1C7', borderRadius: 12, padding: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10, color: '#5F5E5A' }}>Live Activity</div>
                  {events.slice(0, 5).map((e, i) => (
                    <div key={i} style={{ fontSize: 11, color: '#5F5E5A', padding: '4px 0', borderBottom: '1px solid #E8E6E0' }}>
                      <span style={{ color: '#1D9E75', fontWeight: 600 }}>{e.event}</span>
                      {' — '}{e.phaCode} {e.pdfType ? `(${e.pdfType})` : ''}
                      <span style={{ float: 'right', color: '#888885' }}>{new Date(e.timestamp).toLocaleTimeString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}